import React, { useContext, useState } from "react";
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { Id } from "../../../convex/_generated/dataModel";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import clsx from "clsx";
import { SkillProgressWidget } from "./widgets/SkillProgressWidget";
import { MindsetScoresWidget } from "./widgets/MindsetScoresWidget";
import { SessionRecommendationWidget } from "./widgets/SessionRecommendationWidget";
import { QuickRepliesWidget } from "./widgets/QuickRepliesWidget";
import { ActionButtonsWidget } from "./widgets/ActionButtonsWidget";
import { CompletedSessionsWidget } from "./widgets/CompletedSessionsWidget";
import { ActiveSessionWidget } from "./widgets/ActiveSessionWidget";
import { RelevantSessionsWidget } from "./widgets/RelevantSessionsWidget";
import { FeedbackFormWidget } from "./widgets/FeedbackFormWidget";
import { BaseContext } from "../../../shared/base-context";
import { Divider } from "../../../catalyst-components/divider";
import { Button } from "../../../catalyst-components/button";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolidIcon, HandThumbDownIcon as HandThumbDownSolidIcon } from "@heroicons/react/24/solid";
import { parseMarkdown } from "../../../shared/helpers/markdown";

// Reverse mapping: widget type → tool name that produces it
const WIDGET_TYPE_TO_TOOL: Record<string, string> = {
  mindset_scores: "get_mindset_scores",
  skill_progress: "get_skill_progress",
  session_recommendation: "get_recommended_sessions",
  active_session: "get_recommended_sessions",
  completed_sessions: "get_completed_sessions",
  relevant_sessions: "find_relevant_sessions",
};

// Widget types that support feedback (tool-produced, not UI-only)
const FEEDBACKABLE_WIDGET_TYPES = new Set(Object.keys(WIDGET_TYPE_TO_TOOL));

interface DebugTraceEntry {
  type: string;
  tool_name?: string;
  tool_args?: any;
  result?: any;
  message?: string;
  timestamp: string;
}

interface EvergreenMessageData {
  _id: Id<"evergreen_messages">;
  user_id: Id<"users">;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  // Legacy single widget (deprecated)
  widget?: {
    type: string;
    data: any;
  };
  // Multiple widgets support
  widgets?: Array<{
    type: string;
    data: any;
  }>;
  debug_trace?: DebugTraceEntry[];
  context_id?: string;
  hidden?: boolean;
}

interface Props {
  message: EvergreenMessageData;
  userName: string;
  onQuickReply?: (reply: string) => void;
  isStreaming?: boolean;
}

export const EvergreenMessage: React.FC<Props> = ({ message, userName, onQuickReply, isStreaming }) => {
  const { debug, convexUserId } = useContext(BaseContext);
  const isUser = message.role === "user";

  if (message.hidden || message.role === "system") return null;

  // Combine legacy widget with new widgets array for backward compatibility
  const allWidgets: Array<{ type: string; data: any }> = [];
  if (message.widgets && message.widgets.length > 0) {
    allWidgets.push(...message.widgets);
  } else if (message.widget) {
    // Legacy support: if only old widget field exists
    allWidgets.push(message.widget);
  }

  // Only show widgets when streaming is complete to prevent UI jitter
  const showWidgets = allWidgets.length > 0 && !isStreaming;

  const containerClass = clsx(
    isUser ? "ml-auto" : "mr-auto",
    "w-full lg:w-2/3 mb-4"
  );
  const nameClass = clsx(
    isUser ? "text-right" : "text-left",
    "font-semibold text-foreground"
  );
  const bodyClass = clsx(
    isUser ? "text-right" : "text-left",
    "text-foreground"
  );

  return (
    <article className={containerClass}>
      <div className={nameClass}>
        {isUser ? userName : "Gruntled"}
      </div>
      {isUser ? (
        <p className={bodyClass}>{message.content}</p>
      ) : (
        <div
          className={bodyClass}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
        />
      )}

      {/* Render all widgets - only after streaming completes */}
      {showWidgets && (
        <div className="mt-3 space-y-3 lg:max-w-lg">
          {allWidgets.map((widget, index) => (
            <div key={`${widget.type}-${index}`}>
              <WidgetRenderer
                widget={widget}
                onQuickReply={onQuickReply || (() => {})}
              />
              {FEEDBACKABLE_WIDGET_TYPES.has(widget.type) && convexUserId && (
                <WidgetFeedbackButtons
                  messageId={message._id}
                  userId={convexUserId as Id<"users">}
                  widgetType={widget.type}
                  toolName={WIDGET_TYPE_TO_TOOL[widget.type]}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Debug trace - only show for assistant messages when debug mode is on */}
      {debug && !isUser && message.debug_trace && message.debug_trace.length > 0 && (
        <DebugTraceDisplay trace={message.debug_trace} />
      )}
    </article>
  );
};

// Thumbs up/down feedback buttons for a widget
interface WidgetFeedbackButtonsProps {
  messageId: Id<"evergreen_messages">;
  userId: Id<"users">;
  widgetType: string;
  toolName: string;
}

const WidgetFeedbackButtons: React.FC<WidgetFeedbackButtonsProps> = ({
  messageId,
  userId,
  widgetType,
  toolName,
}) => {
  const submitFeedback = useMutation(api.widget_feedback.submit);
  const feedbackMap = useQuery(api.widget_feedback.getForMessage, {
    user_id: userId,
    message_id: messageId,
  });

  const currentFeedback = feedbackMap?.[widgetType];

  const handleFeedback = async (feedback: "positive" | "negative") => {
    await submitFeedback({
      user_id: userId,
      message_id: messageId,
      tool_name: toolName,
      widget_type: widgetType,
      feedback,
    });
  };

  return (
    <div className="flex items-center gap-1 mt-1.5 ml-1">
      <button
        onClick={() => handleFeedback("positive")}
        className={clsx(
          "p-1 rounded-md transition-colors",
          currentFeedback === "positive"
            ? "text-emerald-600 bg-emerald-50"
            : "text-muted-foreground/50 hover:text-emerald-600 hover:bg-emerald-50/50"
        )}
        title="Helpful"
      >
        {currentFeedback === "positive" ? (
          <HandThumbUpSolidIcon className="h-3.5 w-3.5" />
        ) : (
          <HandThumbUpIcon className="h-3.5 w-3.5" />
        )}
      </button>
      <button
        onClick={() => handleFeedback("negative")}
        className={clsx(
          "p-1 rounded-md transition-colors",
          currentFeedback === "negative"
            ? "text-red-500 bg-red-50"
            : "text-muted-foreground/50 hover:text-red-500 hover:bg-red-50/50"
        )}
        title="Not helpful"
      >
        {currentFeedback === "negative" ? (
          <HandThumbDownSolidIcon className="h-3.5 w-3.5" />
        ) : (
          <HandThumbDownIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
};

export interface WidgetRendererProps {
  widget: {
    type: string;
    data: any;
  };
  onQuickReply: (reply: string) => void;
}

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widget, onQuickReply }) => {
  if (!widget || !widget.type) return null;

  switch (widget.type) {
    case "skill_progress":
      return <SkillProgressWidget data={widget.data} />;
    case "mindset_scores":
      return <MindsetScoresWidget data={widget.data} />;
    case "session_recommendation":
      return <SessionRecommendationWidget data={widget.data} />;
    case "completed_sessions":
      return <CompletedSessionsWidget data={widget.data} />;
    case "active_session":
      return <ActiveSessionWidget data={widget.data} />;
    case "relevant_sessions":
      return <RelevantSessionsWidget data={widget.data} />;
    case "quick_replies":
      return <QuickRepliesWidget data={widget.data} onSelect={onQuickReply} />;
    case "action_buttons":
      return <ActionButtonsWidget data={widget.data} />;
    case "feedback_form":
      return <FeedbackFormWidget data={widget.data} />;
    default:
      return null;
  }
};

interface DebugTraceDisplayProps {
  trace: DebugTraceEntry[];
}

export const DebugTraceDisplay: React.FC<DebugTraceDisplayProps> = ({ trace }) => {
  const [expanded, setExpanded] = useState(false);

  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getEntryIcon = (type: string) => {
    switch (type) {
      case "tool_call": return "→";
      case "tool_result": return "←";
      case "decision": return "•";
      default: return "·";
    }
  };

  return (
    <section className="bg-muted/50 p-4 mt-4 rounded-xl border border-border/30">
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold text-sm text-foreground">Agent Debug Trace</span>
        <Button color="secondary" className="text-xs py-1 px-2 rounded-full" onClick={() => setExpanded(!expanded)}>
          {expanded ? "Collapse" : "Expand"} ({trace.length} steps)
        </Button>
      </div>

      {expanded && (
        <>
          <Divider className="my-2" />
          <div className="space-y-2 text-sm">
            {trace.map((entry, index) => (
              <div key={index} className="border-l-2 border-primary/30 pl-3">
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground">{getEntryIcon(entry.type)}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    [{formatTimestamp(entry.timestamp)}]
                  </span>
                  <span className="font-semibold capitalize text-foreground">{entry.type.replace("_", " ")}</span>
                </div>

                {entry.type === "tool_call" && (
                  <div className="ml-6 mt-1">
                    <span className="font-mono bg-muted px-1 rounded text-foreground">{entry.tool_name}</span>
                    {entry.tool_args && Object.keys(entry.tool_args).length > 0 && (
                      <pre className="text-xs bg-muted p-2 mt-1 rounded-lg overflow-x-auto text-foreground">
                        {JSON.stringify(entry.tool_args, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                {entry.type === "tool_result" && (
                  <div className="ml-6 mt-1">
                    <span className="text-xs text-muted-foreground">Result from </span>
                    <span className="font-mono bg-muted px-1 rounded text-xs text-foreground">{entry.tool_name}</span>
                    {entry.result && (
                      <pre className="text-xs bg-muted p-2 mt-1 rounded-lg overflow-x-auto max-h-32 overflow-y-auto text-foreground">
                        {JSON.stringify(entry.result, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                {entry.type === "decision" && entry.message && (
                  <div className="ml-6 mt-1 text-muted-foreground italic">
                    {entry.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!expanded && (
        <div className="text-xs text-muted-foreground">
          {trace.filter(t => t.type === "tool_call").length} tool calls •
          {" "}{trace.filter(t => t.type === "decision").length} decisions
        </div>
      )}
    </section>
  );
};
